import React, { useState } from "react";
import {
    Button,
    Spin,
    Row,
    Col,
    Tooltip,
    theme,
    Space,
    Typography,
    Tag,
} from "antd";
import {
    usePlans,
    useOrganizationTrialStatus,
} from "../../_actions/mutations/subscription";
import { useCurrentUser } from "../../actions/auth";
import {
    CheckOutlined,
    StarFilled,
    ThunderboltFilled,
    RocketFilled,
} from "@ant-design/icons";

const { Title, Text } = Typography;

const BILLING_MONTHLY = 1;
const BILLING_YEARLY = 12;

const PlanIcon = ({ index }) => {
    const icons = [
        <ThunderboltFilled style={{ fontSize: 18 }} />,
        <StarFilled style={{ fontSize: 18 }} />,
        <RocketFilled style={{ fontSize: 18 }} />,
    ];
    return icons[index % icons.length];
};

const SubscriptionPlan = ({
    onSubscribe,
    onStartTrial,
    onOneTimePurchase,
    loadingSubscription,
}) => {
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [hoveredPlanId, setHoveredPlanId] = useState(null);
    const [billingCycle, setBillingCycle] = useState(BILLING_MONTHLY);
    const { token } = theme.useToken();

    const { data: plans = [], isLoading } = usePlans();
    const { data: currentUser } = useCurrentUser();
    const organizationId = currentUser?.selected_org?.id;
    const { data: trialData } = useOrganizationTrialStatus(organizationId);
    const trialUsed = trialData?.trial;

    const filteredPlans = plans.filter(
        (plan) => plan.billing_cycle_month === billingCycle,
    );

    const monthlyPlans = plans.filter(
        (p) => p.billing_cycle_month === BILLING_MONTHLY,
    );
    const yearlyPlans = plans.filter(
        (p) => p.billing_cycle_month === BILLING_YEARLY,
    );
    const hasSavings = monthlyPlans.length > 0 && yearlyPlans.length > 0;

    const getSavingsPercent = () => {
        if (
            !hasSavings ||
            monthlyPlans.length === 0 ||
            yearlyPlans.length === 0
        )
            return null;
        const avgMonthly =
            monthlyPlans.reduce((s, p) => s + p.price, 0) / monthlyPlans.length;
        const avgYearlyPerMonth =
            yearlyPlans.reduce((s, p) => s + p.price, 0) /
            yearlyPlans.length /
            12;
        const pct = Math.round((1 - avgYearlyPerMonth / avgMonthly) * 100);
        return pct > 0 ? pct : null;
    };

    const savingsPct = getSavingsPercent();

    if (isLoading) {
        return (
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    padding: "60px 0",
                    gap: 16,
                }}
            >
                <Spin size="large" />
                <Text style={{ color: token.colorTextTertiary, fontSize: 14 }}>
                    Loading plans…
                </Text>
            </div>
        );
    }

    const primaryHex = token.colorPrimary;

    return (
        <div style={{ padding: "8px 4px 4px" }}>
            {/* Billing Toggle */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 32,
                }}
            >
                <div
                    style={{
                        display: "flex",
                        background: token.colorFillQuaternary,
                        borderRadius: 100,
                        padding: 4,
                        gap: 2,
                    }}
                >
                    {[
                        { label: "Monthly", value: BILLING_MONTHLY },
                        { label: "Yearly", value: BILLING_YEARLY },
                    ].map((opt) => (
                        <button
                            key={opt.value}
                            onClick={() => setBillingCycle(opt.value)}
                            style={{
                                padding: "8px 24px",
                                borderRadius: 100,
                                border: "none",
                                cursor: "pointer",
                                fontSize: 14,
                                fontWeight:
                                    billingCycle === opt.value ? 600 : 400,
                                background:
                                    billingCycle === opt.value
                                        ? "#fff"
                                        : "transparent",
                                color:
                                    billingCycle === opt.value
                                        ? primaryHex
                                        : token.colorTextSecondary,
                                boxShadow:
                                    billingCycle === opt.value
                                        ? "0 2px 8px rgba(0,0,0,0.1)"
                                        : "none",
                                transition: "all 0.25s ease",
                            }}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
                {savingsPct && billingCycle === BILLING_YEARLY && (
                    <Tag
                        color="success"
                        style={{
                            borderRadius: 100,
                            fontSize: 12,
                            fontWeight: 600,
                            padding: "2px 10px",
                            border: "none",
                        }}
                    >
                        Save {savingsPct}%
                    </Tag>
                )}
                {savingsPct && billingCycle === BILLING_MONTHLY && (
                    <Text style={{ fontSize: 12, color: token.colorSuccess }}>
                        Save {savingsPct}% yearly
                    </Text>
                )}
            </div>

            {/* Plan Cards */}
            <Row gutter={[20, 20]} justify="center" align="middle">
                {filteredPlans.map((plan, index) => {
                    const isSelected = selectedPlan?.id === plan.id;
                    const isPopular = index === 1;
                    const isHovered = hoveredPlanId === plan.id;

                    const cardScale = isSelected
                        ? "scale(1.03) translateY(-6px)"
                        : isPopular
                          ? isHovered
                              ? "scale(1.025) translateY(-4px)"
                              : "scale(1.015)"
                          : isHovered
                            ? "translateY(-4px)"
                            : "none";

                    const cardShadow = isSelected
                        ? `0 20px 48px -8px ${primaryHex}33, 0 8px 16px -4px rgba(0,0,0,0.08)`
                        : isPopular
                          ? isHovered
                              ? `0 16px 40px -8px ${primaryHex}22, 0 6px 12px -2px rgba(0,0,0,0.06)`
                              : `0 8px 24px -4px ${primaryHex}1a, 0 4px 8px -2px rgba(0,0,0,0.04)`
                          : isHovered
                            ? "0 12px 32px -8px rgba(0,0,0,0.12)"
                            : "0 2px 8px rgba(0,0,0,0.04)";

                    const cardBorder = isSelected
                        ? `2px solid ${primaryHex}`
                        : isPopular
                          ? `2px solid ${primaryHex}55`
                          : `1.5px solid ${token.colorBorderSecondary}`;

                    return (
                        <Col key={plan.id} xs={24} sm={12} md={8}>
                            <div
                                onClick={() => setSelectedPlan(plan)}
                                onMouseEnter={() => setHoveredPlanId(plan.id)}
                                onMouseLeave={() => setHoveredPlanId(null)}
                                style={{
                                    position: "relative",
                                    borderRadius: 20,
                                    border: cardBorder,
                                    background: isSelected
                                        ? `linear-gradient(145deg, #fff 60%, ${primaryHex}08)`
                                        : "#fff",
                                    cursor: "pointer",
                                    overflow: "hidden",
                                    transform: cardScale,
                                    boxShadow: cardShadow,
                                    transition:
                                        "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                {/* Top accent bar for popular */}
                                {isPopular && (
                                    <div
                                        style={{
                                            height: 4,
                                            background: `linear-gradient(90deg, ${primaryHex}aa, ${primaryHex}, ${primaryHex}aa)`,
                                            borderRadius: "20px 20px 0 0",
                                        }}
                                    />
                                )}

                                {/* Popular badge — centered tab hanging from top edge */}
                                {isPopular && (
                                    <div
                                        style={{
                                            position: "absolute",
                                            top: -1,
                                            left: "50%",
                                            transform: "translateX(-50%)",
                                            background: `linear-gradient(135deg, ${primaryHex}, ${primaryHex}cc)`,
                                            color: "#fff",
                                            padding: "3px 14px",
                                            borderRadius: "0 0 10px 10px",
                                            fontSize: 11,
                                            fontWeight: 700,
                                            letterSpacing: "0.05em",
                                            textTransform: "uppercase",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 4,
                                            whiteSpace: "nowrap",
                                            zIndex: 2,
                                        }}
                                    >
                                        <StarFilled style={{ fontSize: 9 }} />{" "}
                                        Most Popular
                                    </div>
                                )}

                                {/* Selected check */}
                                {isSelected && (
                                    <div
                                        style={{
                                            position: "absolute",
                                            top: 14,
                                            left: 16,
                                            width: 22,
                                            height: 22,
                                            borderRadius: "50%",
                                            background: primaryHex,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <CheckOutlined
                                            style={{
                                                color: "#fff",
                                                fontSize: 11,
                                            }}
                                        />
                                    </div>
                                )}

                                <div
                                    style={{
                                        padding: "28px 24px 24px",
                                        flex: 1,
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                >
                                    {/* Plan icon + name */}
                                    <div
                                        style={{
                                            textAlign: "center",
                                            marginBottom: 20,
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: 48,
                                                height: 48,
                                                borderRadius: 14,
                                                background: isSelected
                                                    ? primaryHex
                                                    : `${primaryHex}15`,
                                                color: isSelected
                                                    ? "#fff"
                                                    : primaryHex,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                margin: "0 auto 12px",
                                                transition: "all 0.3s ease",
                                            }}
                                        >
                                            <PlanIcon index={index} />
                                        </div>
                                        <Title
                                            level={4}
                                            style={{
                                                margin: 0,
                                                color: isSelected
                                                    ? primaryHex
                                                    : token.colorText,
                                                fontWeight: 700,
                                                letterSpacing: "-0.01em",
                                            }}
                                        >
                                            {plan.plan_name}
                                        </Title>
                                    </div>

                                    {/* Price */}
                                    <div
                                        style={{
                                            textAlign: "center",
                                            paddingBottom: 20,
                                            marginBottom: 20,
                                            borderBottom: `1px solid ${token.colorBorderSecondary}`,
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "flex-start",
                                                justifyContent: "center",
                                                gap: 2,
                                            }}
                                        >
                                            <Text
                                                style={{
                                                    fontSize: 20,
                                                    fontWeight: 600,
                                                    color: primaryHex,
                                                    lineHeight: "38px",
                                                }}
                                            >
                                                ₹
                                            </Text>
                                            <span
                                                style={{
                                                    fontSize: 44,
                                                    fontWeight: 800,
                                                    color: primaryHex,
                                                    lineHeight: 1,
                                                    letterSpacing: "-0.03em",
                                                }}
                                            >
                                                {billingCycle === BILLING_YEARLY
                                                    ? Math.round(
                                                          plan.price / 12,
                                                      ).toLocaleString()
                                                    : plan.price.toLocaleString()}
                                            </span>
                                        </div>
                                        <Text
                                            style={{
                                                fontSize: 13,
                                                color: token.colorTextTertiary,
                                                display: "block",
                                                marginTop: 4,
                                            }}
                                        >
                                            {billingCycle === BILLING_YEARLY
                                                ? `₹${plan.price.toLocaleString()} billed yearly`
                                                : "per month"}
                                        </Text>
                                    </div>

                                    {/* Features */}
                                    <div
                                        style={{
                                            flex: 1,
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 12,
                                        }}
                                    >
                                        {plan.features?.map((feature, idx) => (
                                            <div
                                                key={idx}
                                                style={{
                                                    display: "flex",
                                                    gap: 10,
                                                    alignItems: "flex-start",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        minWidth: 20,
                                                        height: 20,
                                                        borderRadius: 6,
                                                        background: isSelected
                                                            ? primaryHex
                                                            : `${primaryHex}18`,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent:
                                                            "center",
                                                        marginTop: 1,
                                                        transition:
                                                            "all 0.3s ease",
                                                    }}
                                                >
                                                    <CheckOutlined
                                                        style={{
                                                            color: isSelected
                                                                ? "#fff"
                                                                : primaryHex,
                                                            fontSize: 10,
                                                            transition:
                                                                "all 0.3s ease",
                                                        }}
                                                    />
                                                </div>
                                                <Tooltip
                                                    title={feature.detail}
                                                    placement="right"
                                                >
                                                    <Text
                                                        style={{
                                                            fontSize: 13.5,
                                                            color: token.colorTextSecondary,
                                                            cursor: feature.detail
                                                                ? "help"
                                                                : "default",
                                                            lineHeight: "20px",
                                                        }}
                                                    >
                                                        {feature.title}
                                                    </Text>
                                                </Tooltip>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Inline CTA hint */}
                                    <div
                                        style={{
                                            marginTop: 24,
                                            textAlign: "center",
                                            opacity: isSelected
                                                ? 0
                                                : isHovered
                                                  ? 1
                                                  : 0,
                                            transform:
                                                isHovered && !isSelected
                                                    ? "translateY(0)"
                                                    : "translateY(4px)",
                                            transition: "all 0.2s ease",
                                            pointerEvents: "none",
                                        }}
                                    >
                                        <Text
                                            style={{
                                                fontSize: 12,
                                                color: primaryHex,
                                                fontWeight: 500,
                                            }}
                                        >
                                            Click to select
                                        </Text>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    );
                })}
            </Row>

            {/* Bottom CTA */}
            <div
                style={{
                    marginTop: 36,
                    padding: "24px 20px",
                    background: token.colorFillQuaternary,
                    borderRadius: 16,
                    border: `1px solid ${token.colorBorderSecondary}`,
                }}
            >
                {selectedPlan ? (
                    <div style={{ textAlign: "center", marginBottom: 16 }}>
                        <Text
                            style={{
                                fontSize: 13,
                                color: token.colorTextSecondary,
                            }}
                        >
                            Selected:{" "}
                            <Text strong style={{ color: token.colorText }}>
                                {selectedPlan.plan_name}
                            </Text>
                            {" — "}
                            <Text
                                style={{
                                    color: token.colorPrimary,
                                    fontWeight: 600,
                                }}
                            >
                                ₹
                                {billingCycle === BILLING_YEARLY
                                    ? Math.round(
                                          selectedPlan.price / 12,
                                      ).toLocaleString()
                                    : selectedPlan.price.toLocaleString()}
                                /mo
                            </Text>
                        </Text>
                    </div>
                ) : (
                    <div style={{ textAlign: "center", marginBottom: 16 }}>
                        <Text
                            style={{
                                fontSize: 13,
                                color: token.colorTextTertiary,
                            }}
                        >
                            Select a plan above to continue
                        </Text>
                    </div>
                )}

                <Space
                    size={12}
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        flexWrap: "wrap",
                    }}
                >
                    <Button
                        type="primary"
                        size="large"
                        disabled={!selectedPlan}
                        loading={loadingSubscription}
                        onClick={() => onSubscribe(selectedPlan)}
                        style={{
                            height: 48,
                            fontSize: 15,
                            fontWeight: 600,
                            padding: "0 32px",
                            borderRadius: 12,
                            background: selectedPlan
                                ? `linear-gradient(135deg, ${token.colorPrimary}, ${token.colorPrimaryActive || token.colorPrimary}dd)`
                                : undefined,
                            border: "none",
                            boxShadow: selectedPlan
                                ? `0 4px 16px ${token.colorPrimary}44`
                                : "none",
                            transition: "all 0.3s ease",
                        }}
                    >
                        {selectedPlan
                            ? `Subscribe to ${selectedPlan.plan_name}`
                            : "Choose a Plan"}
                    </Button>

                    {!trialUsed && (
                        <Button
                            size="large"
                            disabled={!selectedPlan}
                            loading={loadingSubscription}
                            onClick={() => onStartTrial(selectedPlan)}
                            style={{
                                height: 48,
                                fontSize: 15,
                                fontWeight: 500,
                                padding: "0 28px",
                                borderRadius: 12,
                                border: `1.5px solid ${token.colorBorder}`,
                                background: "#fff",
                                transition: "all 0.25s ease",
                            }}
                        >
                            Start Free Trial
                        </Button>
                    )}

                    <Button
                        size="large"
                        disabled={!selectedPlan}
                        loading={loadingSubscription}
                        onClick={() => onOneTimePurchase(selectedPlan)}
                        style={{
                            height: 48,
                            fontSize: 15,
                            fontWeight: 500,
                            padding: "0 28px",
                            borderRadius: 12,
                            border: `1.5px dashed ${token.colorBorder}`,
                            background: "transparent",
                            transition: "all 0.25s ease",
                        }}
                    >
                        One-Time Purchase
                    </Button>
                </Space>
            </div>
        </div>
    );
};

export default SubscriptionPlan;
